import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLStructuredData = () => {
  return (
    <div className="premium-course-content">
      <div className="content-hero">
        <div className="hero-badge">SEO & Discovery</div>
        <h1>Structured Data & Schema Markup</h1>
        <p className="hero-lead">
          Enhance your search results with rich snippets. Learn how to implement Schema.org
          structured data to display ratings, prices, recipes, events, and more in search results.
        </p>
      </div>

      <div className="content-section">
        <h2>What is Structured Data?</h2>
        <p>
          Structured data is standardized code that helps search engines understand your content
          and display rich results (rich snippets) in search.
        </p>

        <div className="benefits-grid">
          <div className="benefit-card">
            <h3> Rich Snippets</h3>
            <p>Stand out in search with stars, images, prices, and more</p>
          </div>
          <div className="benefit-card">
            <h3> Knowledge Graph</h3>
            <p>Appear in Google's Knowledge Panel</p>
          </div>
          <div className="benefit-card">
            <h3> Better CTR</h3>
            <p>Rich results get 30% more clicks</p>
          </div>
          <div className="benefit-card">
            <h3> Voice Search</h3>
            <p>Optimize for voice assistants</p>
          </div>
        </div>
      </div>

      <div className="content-section">
        <h2>JSON-LD Format (Recommended)</h2>
        <p>
          Google recommends JSON-LD format - easy to implement, doesn't clutter HTML,
          and can be dynamically generated.
        </p>

        <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Article Title</title>
    
    <!-- JSON-LD structured data --&gt;
    <script type="application/ld+json">
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Complete Guide to Image Compression",
      "author": {
        "@type": "Person",
        "name": "John Doe"
      },
      "datePublished": "2024-01-15",
      "image": "https://example.com/image.jpg"
    }
    </script>
</head>
<body>
    <!-- Regular HTML content --&gt;
</body>
</html>`} />
      </div>

      <div className="content-section">
        <h2>Article Schema</h2>

        <h3>Blog Post / Tutorial</h3>
        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "How to Compress Images Without Losing Quality",
  "description": "Learn the best techniques for image compression",
  "image": "https://www.filefusion.app/images/compression-guide.jpg",
  "author": {
    "@type": "Person",
    "name": "Jane Smith",
    "url": "https://www.filefusion.app/authors/jane-smith"
  },
  "publisher": {
    "@type": "Organization",
    "name": "FileFusion",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.filefusion.app/logo.png"
    }
  },
  "datePublished": "2024-01-15",
  "dateModified": "2024-01-20",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://www.filefusion.app/insights/courses/image-compression"
  }
}
</script>`} />

        <h3>Benefits:</h3>
        <ul>
          <li>Article rich snippet in search results</li>
          <li>Shows author, date, and image</li>
          <li>Eligible for Google News</li>
          <li>Better mobile search display</li>
        </ul>
      </div>

      <div className="content-section">
        <h2>Product Schema</h2>

        <h3>E-commerce Product</h3>
        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Premium Image Compression Software",
  "image": "https://www.filefusion.app/images/product.jpg",
  "description": "Professional image compression tool for businesses",
  "brand": {
    "@type": "Brand",
    "name": "FileFusion"
  },
  "offers": {
    "@type": "Offer",
    "url": "https://www.filefusion.app/products/premium",
    "priceCurrency": "USD",
    "price": "49.99",
    "priceValidUntil": "2024-12-31",
    "availability": "https://schema.org/InStock",
    "seller": {
      "@type": "Organization",
      "name": "FileFusion"
    }
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "235"
  }
}
</script>`} />

        <h3>Rich Snippet Shows:</h3>
        <ul>
          <li> Star rating (4.8/5)</li>
          <li> Price ($49.99)</li>
          <li>Availability (In Stock)</li>
          <li> Review count (235 reviews)</li>
          <li> Product image</li>
        </ul>
      </div>

      <div className="content-section">
        <h2>Recipe Schema</h2>

        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Recipe",
  "name": "Chocolate Chip Cookies",
  "image": "https://example.com/cookies.jpg",
  "description": "Delicious homemade chocolate chip cookies",
  "author": {
    "@type": "Person",
    "name": "Chef Maria"
  },
  "datePublished": "2024-01-15",
  "prepTime": "PT15M",
  "cookTime": "PT12M",
  "totalTime": "PT27M",
  "recipeYield": "24 cookies",
  "recipeCategory": "Dessert",
  "recipeCuisine": "American",
  "keywords": "cookies, chocolate, dessert",
  "nutrition": {
    "@type": "NutritionInformation",
    "calories": "150 calories"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "ratingCount": "482"
  },
  "recipeIngredient": [
    "2 cups all-purpose flour",
    "1 cup butter",
    "2 cups chocolate chips"
  ],
  "recipeInstructions": [
    {
      "@type": "HowToStep",
      "text": "Preheat oven to 350F (175C)"
    },
    {
      "@type": "HowToStep",
      "text": "Mix butter and sugar until creamy"
    }
  ]
}
</script>`} />

        <h3>Rich Snippet Shows:</h3>
        <ul>
          <li> Rating (4.9/5 from 482 reviews)</li>
          <li> Time (Prep: 15min, Cook: 12min, Total: 27min)</li>
          <li> Calories (150 per serving)</li>
          <li> Recipe image</li>
        </ul>
      </div>

      <div className="content-section">
        <h2>FAQ Schema</h2>

        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How does image compression work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Image compression reduces file size by removing redundant data. Lossy compression (JPEG) discards some visual information, while lossless compression (PNG) preserves all original data."
      }
    },
    {
      "@type": "Question",
      "name": "What's the best image format for web?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "WebP is generally best for web, offering 25-35% smaller file sizes than JPEG with similar quality. However, JPEG remains widely supported and is a good fallback."
      }
    },
    {
      "@type": "Question",
      "name": "Can I compress images without losing quality?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, using lossless compression formats like PNG or lossless WebP. However, lossy compression (JPEG, lossy WebP) achieves much smaller file sizes with minimal visible quality loss."
      }
    }
  ]
}
</script>`} />

        <h3>Benefits:</h3>
        <ul>
          <li>Questions appear in search results</li>
          <li>Expandable Q&A in Google</li>
          <li>Featured in "People also ask" section</li>
          <li>Increases visibility</li>
        </ul>
      </div>

      <div className="content-section">
        <h2>How-To Schema</h2>

        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Compress an Image",
  "description": "Step-by-step guide to compress images online",
  "image": "https://www.filefusion.app/images/how-to.jpg",
  "totalTime": "PT5M",
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "USD",
    "value": "0"
  },
  "tool": [
    {
      "@type": "HowToTool",
      "name": "FileFusion Image Compressor"
    }
  ],
  "step": [
    {
      "@type": "HowToStep",
      "name": "Upload Image",
      "text": "Click 'Upload' and select your image file",
      "image": "https://www.filefusion.app/images/step1.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Choose Quality",
      "text": "Select compression quality (recommend 80-90%)",
      "image": "https://www.filefusion.app/images/step2.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Download Result",
      "text": "Click 'Download' to save compressed image",
      "image": "https://www.filefusion.app/images/step3.jpg"
    }
  ]
}
</script>`} />
      </div>

      <div className="content-section">
        <h2>Organization Schema</h2>

        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "FileFusion",
  "url": "https://filefusion.com",
  "logo": "https://www.filefusion.app/logo.png",
  "description": "Free online file conversion and editing tools",
  "foundingDate": "2020",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-555-123-4567",
    "contactType": "Customer Support",
    "email": "support@filefusion.com",
    "availableLanguage": ["English", "Spanish"]
  },
  "sameAs": [
    "https://twitter.com/filefusion",
    "https://facebook.com/filefusion",
    "https://linkedin.com/company/filefusion"
  ],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "123 Tech Street",
    "addressLocality": "San Francisco",
    "addressRegion": "CA",
    "postalCode": "94105",
    "addressCountry": "US"
  }
}
</script>`} />
      </div>

      <div className="content-section">
        <h2>Breadcrumb Schema</h2>

        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://filefusion.com"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Tutorials",
      "item": "https://www.filefusion.app/tutorials"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "HTML",
      "item": "https://www.filefusion.app/insights/courses/html"
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": "Structured Data",
      "item": "https://www.filefusion.app/insights/courses/html/structured-data"
    }
  ]
}
</script>

<!-- Corresponding HTML --&gt;
<nav aria-label="Breadcrumb">
    <ol>
        <li><a href="/">Home</a></li>
        <li><a href="/tutorials">Tutorials</a></li>
        <li><a href="/insights/courses/html">HTML</a></li>
        <li aria-current="page">Structured Data</li>
    </ol>
</nav>`} />
      </div>

      <div className="content-section">
        <h2>Video Schema</h2>

        <CodeBlock language="html" code={`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": "How to Compress Images Tutorial",
  "description": "Learn image compression techniques in this 10-minute video",
  "thumbnailUrl": "https://www.filefusion.app/videos/thumb.jpg",
  "uploadDate": "2024-01-15",
  "duration": "PT10M30S",
  "contentUrl": "https://www.filefusion.app/videos/compression-tutorial.mp4",
  "embedUrl": "https://youtube.com/embed/abc123",
  "publisher": {
    "@type": "Organization",
    "name": "FileFusion",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.filefusion.app/logo.png"
    }
  }
}
</script>`} />
      </div>

      <div className="content-section">
        <h2>Testing Structured Data</h2>

        <h3>Google Tools</h3>
        <div className="tools-list">
          <ol>
            <li>
              <strong>Rich Results Test:</strong>
              <br />
              <code>https://search.google.com/test/rich-results</code>
              <br />
              <em>Test if your markup is eligible for rich results</em>
            </li>
            <li>
              <strong>Schema Markup Validator:</strong>
              <br />
              <code>https://validator.schema.org</code>
              <br />
              <em>Validate your structured data syntax</em>
            </li>
            <li>
              <strong>Google Search Console:</strong>
              <br />
              <em>Monitor how Google sees your structured data</em>
            </li>
          </ol>
        </div>

        <h3>Common Errors</h3>
        <ul>
          <li> Missing required fields (e.g., image, author)</li>
          <li> Invalid date format (use ISO 8601: YYYY-MM-DD)</li>
          <li> Invalid URL format (must be absolute URLs)</li>
          <li> Mismatched content (structured data doesn't match page)</li>
          <li> JSON syntax errors (missing commas, brackets)</li>
        </ul>
      </div>

      <div className="content-section">
        <h2>Best Practices</h2>

        <div className="best-practices-grid">
          <div className="practice-card">
            <h3>Do</h3>
            <ul>
              <li>Use JSON-LD format (recommended by Google)</li>
              <li>Match structured data to visible content</li>
              <li>Include all required properties</li>
              <li>Use absolute URLs (not relative)</li>
              <li>Test with Google's Rich Results Test</li>
              <li>Keep data accurate and up-to-date</li>
              <li>Use appropriate schema types</li>
            </ul>
          </div>
          <div className="practice-card error">
            <h3> Don't</h3>
            <ul>
              <li>Add invisible content to users</li>
              <li>Mark up content not on the page</li>
              <li>Include misleading information</li>
              <li>Stuff keywords in structured data</li>
              <li>Use wrong schema types</li>
              <li>Forget to update when content changes</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
};

export default HTMLStructuredData;




